"use client";

import { forwardRef, type ReactNode, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
interface ConfirmModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  confirmText?: ReactNode;
  cancelText?: ReactNode;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
  confirmDisabled?: boolean;
  hideCancelButton?: boolean;
  closeOnError?: boolean;
}
export const ConfirmModal = forwardRef<HTMLDivElement, ConfirmModalProps>(function ConfirmModal({
  open: controlledOpen,
  onOpenChange,
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  loading: controlledLoading,
  children,
  className,
  confirmDisabled = false,
  hideCancelButton = false,
  closeOnError = false
}, ref) {
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const isControlled = controlledOpen !== undefined;
  const isLoadingControlled = controlledLoading !== undefined;
  const handleOpenChange = (next: boolean) => {
    // Only handle the close event if it's not already being handled
    if (onOpenChange) onOpenChange(next);
    if (!isControlled) setOpen(next);
    if (!next) setLoading(false);
  };
  const handleConfirm = async (event?: React.MouseEvent) => {
    // Prevent event propagation to avoid affecting parent components
    if (event) {
      event.stopPropagation();
    }
    if (isLoadingControlled) {
      try {
        await onConfirm?.();
      } catch (error) {
        console.error("Error during ConfirmModal's onConfirm execution:", error);
        if (closeOnError) {
          handleOpenChange(false);
        }
        throw error;
      }
      return;
    }
    setLoading(true);
    try {
      await onConfirm?.();
    } catch (error_0) {
      console.error("Error during ConfirmModal's onConfirm execution:", error_0);
      if (closeOnError) {
        handleOpenChange(false);
      }
      throw error_0;
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = (event_0?: React.MouseEvent) => {
    // Prevent event propagation to avoid affecting parent components
    if (event_0) {
      event_0.stopPropagation();
    }
    onCancel?.();
  };

  // 允许外部 ref 获取 DialogContent DOM
  useImperativeHandle(ref, () => dialogContentRef.current as HTMLDivElement);
  return <Dialog open={isControlled ? controlledOpen : open} onOpenChange={open_0 => {
    // Prevent the dialog close from affecting parent components
    // Only handle the close if it's a legitimate user action
    if (!open_0) {
      // Add a small delay to prevent immediate propagation
      setTimeout(() => {
        handleOpenChange(open_0);
      }, 0);
    } else {
      handleOpenChange(open_0);
    }
  }}>
				<DialogContent ref={dialogContentRef} className={cn("gap-0",
    // 响应式宽度: 移动端自适应，PC端固定宽度
    "w-[calc(100vw-2rem)] max-w-107.5 md:w-107.5",
    // 响应式内边距: 移动端较小，PC端较大
    "p-6 md:p-9", className)}>
					<DialogHeader>
						<DialogTitle className={cn(
        // 颜色、字体等响应式无关属性
        "text-[#010101] font-['DINPro']",
        // 响应式相关: 文字大小和行高、对齐
        "text-center font-bold", "text-xl leading-6 md:text-[1.5rem] md:leading-6")}>
							{title || t("confirmModal.defaultTitle")}
						</DialogTitle>
					</DialogHeader>
					<DialogDescription className={cn(
      // 颜色、字体等响应式无关属性
      "text-[#010101]   font-normal",
      // 响应式相关: 文字大小、行高、边距、对齐
      "text-center", "text-sm leading-6 mt-4 md:text-[0.9375rem] md:leading-6 md:mt-6")}>
						{description}
					</DialogDescription>
					{children}
					<DialogFooter className={cn(
      // 布局相关
      "w-full flex flex-col! gap-3 items-center justify-between",
      // 响应式边距
      "mt-6 md:mt-9")}>
						<Button className="w-full" type="button" size="lg" onClick={event_1 => handleConfirm(event_1)} disabled={confirmDisabled || (isLoadingControlled ? controlledLoading : loading)} loading={isLoadingControlled ? controlledLoading : loading}>
							{(isLoadingControlled ? controlledLoading : loading) ? t("confirmModal.processingText") : confirmText || t("confirmModal.continueText")}
						</Button>
						{!hideCancelButton && <Button className="w-full" variant="outline" type="button" size="lg" onClick={event_2 => handleCancel(event_2)} disabled={isLoadingControlled ? controlledLoading : loading}>
								{cancelText || t("confirmModal.cancelText")}
							</Button>}
					</DialogFooter>
				</DialogContent>
			</Dialog>;
});